// Implements: F-UIX-018, F-USG-005, F-USG-006
/**
 * The client of "My usage": three self-only reads (USG.18, RPT.4, USG.19).
 *
 * None of them names a person: whose figures they are is decided by the server from the
 * session, so no request can ask for somebody else's (and none carries an id to tamper
 * with). The reports are read over the server's default day range (the last 366 days,
 * in the zone of the month window), which the answers echo as `from` and `to`.
 *
 * Owner 2026-10-19: the two reports' export twins (`.../export?format=csv|json`, S11b-2) are
 * offered on the same screen, over the same default range and the same self-only rule; a refusal
 * (the row cap's `422`) raises its `ApiError`, never a file holding the error body.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpDownload, httpRequest } from "../../../shared/api/httpClient";
import type { Download } from "../../../shared/api/httpClient";
import type {
  ExportFormat,
  MyConsumptionView,
  MyMockupsView,
  MyUsageExport,
  MyUsageSummaryView,
} from "../../../types/reports";

export const MY_USAGE_SUMMARY_PATH = `${API_BASE}/usage/me/summary`;
export const MY_CONSUMPTION_PATH = `${API_BASE}/reports/me/consumption`;
export const MY_MOCKUPS_PATH = `${API_BASE}/reports/me/mockups`;
export const MY_CONSUMPTION_EXPORT_PATH = `${API_BASE}/reports/me/consumption/export`;
export const MY_MOCKUPS_EXPORT_PATH = `${API_BASE}/reports/me/mockups/export`;

/** A read whose body is missing is a broken answer, not an empty one. */
function required<T>(body: T | undefined, path: string): T {
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

export async function fetchMyUsageSummary(signal?: AbortSignal): Promise<MyUsageSummaryView> {
  return required(await httpRequest<MyUsageSummaryView>(MY_USAGE_SUMMARY_PATH, { signal }), MY_USAGE_SUMMARY_PATH);
}

export async function fetchMyConsumption(signal?: AbortSignal): Promise<MyConsumptionView> {
  return required(await httpRequest<MyConsumptionView>(MY_CONSUMPTION_PATH, { signal }), MY_CONSUMPTION_PATH);
}

export async function fetchMyMockups(signal?: AbortSignal): Promise<MyMockupsView> {
  return required(await httpRequest<MyMockupsView>(MY_MOCKUPS_PATH, { signal }), MY_MOCKUPS_PATH);
}

/** The export of one of the caller's reports, in `format`. */
export function downloadMyUsageExport(report: MyUsageExport, format: ExportFormat): Promise<Download> {
  const path = report === "consumption" ? MY_CONSUMPTION_EXPORT_PATH : MY_MOCKUPS_EXPORT_PATH;
  return httpDownload(`${path}?format=${format}`);
}
